/** @jsx jsx */
import { Title } from '@bottlebooks/gatsby-design-system';
import { Col, Container, Row } from '@bottlebooks/gatsby-theme-base';
import { Trans } from '@lingui/macro';
import { graphql } from 'gatsby';
import { jsx } from 'theme-ui';
import { resolve } from '../../helpers/path';
import BrandAddressSection from '../Brand/BrandAddressSection';
import BrandBanner from '../Brand/BrandBanner';
import BrandDescription from '../Brand/BrandDescription';
import BrandFounded from '../Brand/BrandFounded';
import BrandRegionSection from '../Brand/BrandRegionSection';
import BrandVideos from '../Brand/BrandVideos';
import BrandWinemaker from '../Brand/BrandWinemaker';
import Layout from '../Layout';
import ProducerActions from '../Producer/ProducerActions';
import ProductListListRow from '../ProductListsList/ProductListListRow';
import ProductListsList from '../ProductListsList/ProductListsList';
import ProductListRow from '../ProductsList/ProductListRow';
import ProductsList from '../ProductsList/ProductsList';
import ProducerHeaderRow from './ProducerHeaderRow';

export default function ProducerPage({
  producer,
  products,
  productLists,
  previous,
  next,
  getProductUrl,
  paths = {},
  locale,
}) {
  const hasProducts = products && products.length !== 0;
  const hasProductLists = productLists && productLists.length !== 0;
  const hasProductsSection = hasProducts || hasProductLists;
  return (
    <Layout
      paths={paths}
      locale={locale}
      sx={{ backgroundColor: 'transparent' }}
    >
      <BrandBanner {...producer} />
      <ProducerHeaderRow
        producer={producer}
        previous={previous}
        next={next}
        getProducerPath={slug => slug && resolve([paths.producers, slug])}
      />

      <Container>
        <Row
          sx={{
            paddingY: 3,
            paddingX: [null, `${100 / 12}%`], // 1 col spacing from medium width up.
          }}
        >
          <Col>
            <ProducerActions producer={producer} />
          </Col>
        </Row>

        <Row
          sx={{
            paddingX: [null, `${100 / 12}%`], // 1 col spacing from medium width up.
          }}
        >
          <Col>
            <BrandDescription brand={producer} />
            <BrandWinemaker brand={producer} />
            <BrandRegionSection brand={producer} />
            <BrandFounded brand={producer} />
            <BrandVideos brand={producer} />
          </Col>
        </Row>
      </Container>

      <BrandAddressSection brand={producer} />

      {hasProductsSection ? (
        <Container>
          <Row
            sx={{
              marginY: 5,
              paddingX: [null, `${100 / 12}%`], // 1 col spacing from medium width up.
            }}
          >
            <Col flex>
              <Title sx={{ marginBottom: 4 }}>
                <Trans>Products</Trans>
              </Title>
              {hasProductLists && (
                <ProductListsList sx={{ marginX: [-3, -2], marginY: -2 }}>
                  {productLists.map(productList => (
                    <ProductListListRow
                      key={productList.id}
                      productList={productList}
                      to={resolve([paths.productLists, productList.slug])}
                      sx={{ marginX: [0, 2], marginY: [0, 2] }}
                    />
                  ))}
                </ProductListsList>
              )}

              {hasProducts && (
                <ProductsList sx={{ marginX: [-3, -2], marginY: -2 }}>
                  {products.map(product => (
                    <ProductListRow
                      key={product.id}
                      product={product}
                      to={getProductUrl?.(product)}
                      sx={{ marginX: [0, 2], marginY: [0, 2] }}
                    />
                  ))}
                </ProductsList>
              )}
            </Col>
          </Row>
        </Container>
      ) : null}
    </Layout>
  );
}

export const fragment = graphql`
  fragment ProducerPage_Event on Event {
    name
    ...EventBanner
  }
  fragment ProducerPage_Product on Product {
    id
    slug
    ...ProductListRow
  }
  fragment ProducerPage_ProductList on Event {
    id
    slug
    ...ProductListListRow
  }
  fragment ProducerPage on Producer {
    ...BrandBanner
    ...ProducerHeaderRow
    ...BrandDescription
    ...BrandWinemaker
    ...BrandRegionSection
    ...BrandFounded
    ...BrandAddressSection
    ...BrandVideos
    products {
      exhibitor {
        slug
      }
      ...ProducerPage_Product
    }
  }
`;
